import Link from 'next/link';
import React, { useContext } from 'react';
import cx from 'classnames';
import { OpenAPIObject } from 'openapi3-ts';

import Context from '../../../contexts/docs';
import OpenAPIContext from '../../../contexts/docs/openapi';
import { isApiRoute } from '../utilities';

import styles from './index.module.scss';
import HideFromUser from 'src/components-v2/Helpers/HideFromUser/HideFromUser';
import MarkdownIconButton from '../Header/MarkdownIconButton';

interface Props {
  Component: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  id: string;
  children: React.ReactElement;
  className?: string;
}

const LinkedHeading: React.FC<Props> = ({
  Component,
  id,
  children,
  className,
}) => {
  const { tableOfContents, metadata } = useContext(Context);
  const definition: OpenAPIObject | null = useContext(OpenAPIContext);

  // `children` can be plain text or an MDX component. Since this component introduces its own
  // wrapping `<a>` tag, we need to strip away any underlying Markdown link if it exists.
  const unwrappedChildren =
    children.props?.mdxType === 'a' ? children.props.children : children;

  // Determine if we should show the markdown icon
  // Only show on H4s that correspond to actual API endpoints
  const childrenIsApiRoute = isApiRoute(children, definition);
  const shouldShowMarkdownIcon = Component === 'h4' && childrenIsApiRoute;

  // Webhook and method H4s are written as `#### \`NAME\``, which renders the
  // entire heading as an inline-code element. Treat those as sticky too.
  const childrenIsInlineCode = children?.props?.mdxType === 'inlineCode';

  // Check if this should have sticky styling
  // H4s for endpoints, webhooks, and methods on reference pages are sticky.
  // Can opt out by adding 'no-sticky' class.
  const shouldBeSticky =
    Component === 'h4' &&
    metadata?.layout === 'reference' &&
    (childrenIsApiRoute || childrenIsInlineCode) &&
    !className?.includes('no-sticky');

  // return SSR version of component so that headers are included in initial payload
  if (tableOfContents == null) {
    return (
      <>
        {shouldBeSticky && <HideFromUser>=*=*=*=</HideFromUser>}
        <Link
          href={`#${id}`}
          className={shouldShowMarkdownIcon ? styles.endpointLink : undefined}
        >
          <Component
            className={cx(
              styles.heading,
              shouldBeSticky && styles.stickyHeading,
              shouldShowMarkdownIcon && styles.headingWithIcon,
            )}
            {...(shouldBeSticky && { 'data-endpoint-heading': 'true' })}
          >
            {unwrappedChildren}
            <span
              id={id}
              className={styles.offsetAnchor}
              data-algolia='linkedHeadingAnchor'
            />
            {shouldShowMarkdownIcon && <MarkdownIconButton sectionId={id} />}
          </Component>
        </Link>
      </>
    );
  }

  if (id == null) {
    return <Component>{children}</Component>;
  }
  return (
    <>
      {shouldBeSticky && <HideFromUser>=*=*=*=</HideFromUser>}

      <Link
        href={`#${id}`}
        className={shouldShowMarkdownIcon ? styles.endpointLink : undefined}
      >
        <Component
          className={cx(
            styles.heading,
            shouldBeSticky && styles.stickyHeading,
            childrenIsApiRoute && 'noMargin',
            shouldShowMarkdownIcon && styles.headingWithIcon,
          )}
          {...(shouldBeSticky && { 'data-endpoint-heading': 'true' })}
        >
          {childrenIsApiRoute ? (
            <code>{unwrappedChildren}</code>
          ) : (
            unwrappedChildren
          )}
          <span
            id={id}
            className={styles.offsetAnchor}
            data-algolia='linkedHeadingAnchor'
          />
          {shouldShowMarkdownIcon && <MarkdownIconButton sectionId={id} />}
        </Component>
      </Link>
    </>
  );
};

LinkedHeading.displayName = 'LinkedHeading';

export default LinkedHeading;
